import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePublicSettings } from "../lib/publicSettings";
import { trackFapsCapHit, trackFapsCapNudgeClicked, trackFapsCapNudgeShown, useFapsStore } from "../lib/faps";
import { useSession } from "../lib/session";
import { useServerClockOffset } from "../lib/serverClock";
import { IconClose } from "../control/ui/icons";

/** The one key; its value is the server UTC day the nudge last showed. One key rather
 *  than one per day, so blocked or cleared storage costs a repeat, never a pile of keys. */
const SEEN_KEY = "gp:faps-cap-nudge";
/** Long enough to read, short enough that a television is not wearing a banner. */
const AUTO_DISMISS_MS = 12_000;

const utcDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/**
 * The cap moment, sold. A Basic account taps the crown past today's cap and the tap pays
 * nothing — the one second in the day when Premium's higher cap is worth hearing about,
 * so that is the only second this speaks. The cap is on the PAYOUT alone: fapping is never
 * limited for anybody (the server records the fap before it asks about Faps), and the copy
 * says so, because "capped" on its own reads as "stop".
 *
 * Gated three ways: the store recorded a capped tap (`cappedAt`, set only when the server
 * said `capped`); the account is not Premium (a member at their cap has nothing to buy);
 * and Premium's cap is actually higher than Basic's (an operator who set them equal — or
 * set the Basic cap to 0, which also reports capped — has nothing to sell either).
 *
 * ONCE PER DAY, the server's UTC day (the same day the cap is counted on), remembered in
 * localStorage. After the cap every tap is capped, so once-per-cap-event would be a toast
 * per tap; once per day is the whole nudge. Auto-dismisses: on a television this must
 * never be a banner. Renders nothing while Faps is dark.
 *
 * Copy: rates and caps from settings, as the crown and the number — never money.
 *
 * Analytics: `faps_cap_hit` {tier} once per page load when a capped tap first lands — for
 * BOTH tiers, since a member at the Premium cap is a signal too (the cap is priced right
 * only if some people reach it); then `faps_cap_nudge_shown` / `_clicked` for the toast.
 * The toast is deliberately not a gold_upsell_shown: the dialog it opens fires that.
 */
export function FapsCapNudge({ onGoPremium }: { onGoPremium: () => void }) {
  const cappedAt = useFapsStore((s) => s.cappedAt);
  const settings = usePublicSettings();
  const user = useSession((s) => s.user);
  // The offset dates the once-a-day memory, and only a capped tap reads it — so the
  // sampler runs only from then on. This nudge mounts on every screen and page, and an
  // always-on sampler here was four `/api/ping` a minute from a crown nobody had tapped.
  const offset = useServerClockOffset(undefined, cappedAt !== null);
  const [shownFor, setShownFor] = useState<number | null>(null);
  const hitTracked = useRef(false);

  const faps = settings?.faps;
  const premium = (user?.goldUntil ?? 0) > Date.now();
  const sells = !!faps?.enabled && !premium && faps.fapDailyCapPremium > faps.fapDailyCap;

  useEffect(() => {
    if (cappedAt === null || hitTracked.current) return;
    hitTracked.current = true;
    trackFapsCapHit(premium ? "premium" : "basic");
  }, [cappedAt, premium]);

  useEffect(() => {
    if (cappedAt === null || !sells) return;
    const day = utcDay(Date.now() + offset);
    let seen: string | null = null;
    try {
      seen = localStorage.getItem(SEEN_KEY);
    } catch {
      // Blocked storage: shown once per page load, which is the honest fallback.
    }
    if (seen === day) return;
    try {
      localStorage.setItem(SEEN_KEY, day);
    } catch {
      /* same */
    }
    trackFapsCapNudgeShown();
    setShownFor(cappedAt);
    const t = setTimeout(() => setShownFor(null), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [cappedAt, sells, offset]);

  if (shownFor === null || !faps) return null;

  return createPortal(
    <div
      role="status"
      className="fixed bottom-20 right-3 z-[55] w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border border-[#f5c518]/40 bg-[var(--color-panel)] p-4 shadow-2xl"
    >
      <button
        type="button"
        onClick={() => setShownFor(null)}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded p-1 text-zinc-500 hover:bg-white/10 hover:text-white"
      >
        <IconClose width={14} height={14} />
      </button>
      {/* The cap is on the PAYOUT, never on fapping — say so, or "capped" reads as "stop".
          Fapping saves the clip and counts on it exactly as before; only today's Faps
          from it are done. The cap itself is a CLIP count now (2026-09-09), not a Faps
          amount, so it is quoted as plain text — FapsAmount's crown is reserved for an
          actual currency value, and a crown beside a clip count would misread as one. */}
      <p className="pr-6 text-sm text-zinc-100">Today&rsquo;s clips for Faps are capped.</p>
      <p className="mt-1 text-xs text-zinc-400">
        Fap all you like — the extras just won&rsquo;t pay until tomorrow. Premium lords keep
        earning on up to {faps.fapDailyCapPremium.toLocaleString()} clips a day
        {faps.watchMultiplierPremium > 1 || faps.chestMultiplierPremium > 1
          ? ", and earn double from watch time and the chest."
          : "."}
      </p>
      <button
        type="button"
        onClick={() => {
          trackFapsCapNudgeClicked();
          setShownFor(null);
          onGoPremium();
        }}
        className="mt-3 rounded-lg bg-[#f5c518] px-3 py-1.5 text-sm font-semibold text-black hover:brightness-110"
      >
        Go Premium
      </button>
    </div>,
    document.body,
  );
}
